import { Controller } from 'stimulus'
import { trackEvent } from '../../src/javascripts/tracking'

export default class extends Controller {
  static targets = ["content", "spinner"]
  static values = {
    comparisonToken: String,
    generateUrl: String,
    showUrl: String
  }

  connect() {
    // Check if insights already exist, otherwise generate them
    this.loadOrGenerateInsights()
  }

  async loadOrGenerateInsights() {
    try {
      // First try to load existing insights
      const showResponse = await fetch(this.showUrlValue)
      const showData = await showResponse.json()

      if (showData.success) {
        this.displayInsights(showData.insights, showData.expansion_opportunities, showData.confidence_score)
      } else {
        // No existing insights, generate new ones
        await this.generateInsights()
      }
    } catch (error) {
      console.error('Error loading insights:', error)
      await this.generateInsights() // Fallback to generation
    }
  }

  async generateInsights(force = false) {
    try {
      // Show loading state
      this.showLoadingState()

      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      const data = await response.json()

      if (data.success) {
        this.displayInsights(data.insights, data.expansion_opportunities, data.confidence_score)

        // Track insight generation
        trackEvent('Generated AI Comparison Insights', {
          'comparisonToken': this.comparisonTokenValue,
          'confidenceScore': data.confidence_score,
          'expansionOpportunities': data.expansion_opportunities
        });
      } else {
        throw new Error(data.error || 'Failed to generate insights')
      }
    } catch (error) {
      console.error('Error generating insights:', error)
      this.displayError("Unable to generate insights at this time. Your team can still proceed with voting!")
    }
  }

  showLoadingState() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('hidden')
    }
    if (this.hasContentTarget) {
      try {
        this.contentTarget.innerHTML = `
          <div class="flex items-center justify-center py-8">
            <div class="text-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p class="text-gray-600 mt-2">Generating AI insights...</p>
            </div>
          </div>
        `
      } catch (e) {
        this.contentTarget.textContent = 'Loading insights...'
      }
    }
  }

  displayInsights(insights, expansionOpportunities, confidenceScore) {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('hidden')
    }

    if (this.hasContentTarget) {
      try {
        this.contentTarget.innerHTML = this.buildInsightsHTML(insights, expansionOpportunities, confidenceScore)
      } catch (e) {
        this.contentTarget.textContent = 'Unable to display insights at this time.'
      }
    }

    // Track viewing insights
    trackEvent('Viewed AI Comparison Insights', {
      'comparisonToken': this.comparisonTokenValue,
      'confidenceScore': confidenceScore
    });
  }

  buildInsightsHTML(insights, expansionOpportunities, confidenceScore) {
    const insightsArray = [
      { title: 'Decision Guidance', icon: '🎯', content: insights.team_decision_guidance },
      { title: 'Activity Dynamics', icon: '👥', content: insights.activity_dynamics },
      { title: 'Success Tips', icon: '⚡', content: insights.success_optimization }
    ].filter(insight => insight.content)

    const insightsHTML = insightsArray.map(insight => `
      <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div class="flex items-start space-x-3">
          <div class="text-3xl">${insight.icon}</div>
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">${insight.title}</h3>
            <p class="text-gray-700 leading-relaxed">${this.stripMarkdown(insight.content)}</p>
          </div>
        </div>
      </div>
    `).join('')

    const expansionHTML = expansionOpportunities && expansionOpportunities.length > 0 ? `
      <div class="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 class="font-semibold text-blue-900 mb-2">Additional Services We Can Help With:</h4>
        <div class="flex flex-wrap gap-2">
          ${expansionOpportunities.map(opp => `
            <span class="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">${this.formatOpportunity(opp)}</span>
          `).join('')}
        </div>
      </div>
    ` : ''

    return `
      <div class="space-y-4">
        ${insightsHTML}
        ${expansionHTML}
      </div>
    `
  }

  formatOpportunity(opportunity) {
    const mapping = {
      'venue_sourcing': 'Venue Sourcing',
      'catering': 'Catering',
      'refreshments': 'Refreshments',
      'logistics': 'Event Logistics',
      'transport': 'Transport',
      'photography': 'Photography',
      'equipment': 'Equipment Rental'
    }
    return mapping[opportunity] || opportunity.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  stripMarkdown(text) {
    if (!text) return text

    // Remove markdown formatting
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove bold **text**
      .replace(/\*(.*?)\*/g, '$1')      // Remove italic *text*
      .replace(/__(.*?)__/g, '$1')      // Remove bold __text__
      .replace(/_(.*?)_/g, '$1')        // Remove italic _text_
      .replace(/`(.*?)`/g, '$1')        // Remove code `text`
      .trim()
  }

  displayError(message) {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('hidden')
    }

    if (this.hasContentTarget) {
      try {
        this.contentTarget.innerHTML = `
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-center">
              <div class="text-yellow-600 mr-3">⚠️</div>
              <p class="text-yellow-800">${message}</p>
            </div>
          </div>
        `
      } catch (e) {
        this.contentTarget.textContent = message || 'An error occurred.'
      }
    }
  }
}